import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  ShoppingBag, Package, TrendingUp, AlertCircle, Check,
  RefreshCw, Edit2, Save, X, ChevronDown
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUT_CONFIG = {
  en_attente_paiement: { label: 'En attente paiement', color: 'var(--admin-amber)', bg: 'rgba(197,139,25,0.08)' },
  paye:                { label: 'Payé ✓',             color: 'var(--admin-green)', bg: 'rgba(31,157,122,0.08)' },
  en_preparation:      { label: 'En préparation',     color: 'var(--admin-blue)', bg: 'rgba(125,177,255,0.08)' },
  expedie:             { label: 'Expédié',             color: 'var(--admin-teal)', bg: 'rgba(93,188,191,0.08)' },
  livre:               { label: 'Livré',               color: 'var(--admin-green)', bg: 'rgba(31,157,122,0.1)' },
  annule:              { label: 'Annulé',              color: 'var(--admin-red)', bg: 'rgba(229,62,62,0.08)' },
};

export default function AdminBoutique() {
  const [activeTab, setActiveTab] = useState('commandes');
  const [orders, setOrders] = useState([]);
  const [stock, setStock] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState('');
  const [editingStock, setEditingStock] = useState({});
  const [expandedOrder, setExpandedOrder] = useState(null);

  const token = localStorage.getItem('ose_admin_token');
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }),
    [token]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, stockRes, statsRes] = await Promise.all([
        fetch(`${BACKEND}/api/boutique/admin/commandes${filterStatut ? `?statut=${filterStatut}` : ''}`, { headers }),
        fetch(`${BACKEND}/api/boutique/admin/stock`, { headers }),
        fetch(`${BACKEND}/api/boutique/admin/stats`, { headers }),
      ]);
      const [ordersData, stockData, statsData] = await Promise.all([
        ordersRes.json(), stockRes.json(), statsRes.json()
      ]);
      setOrders(ordersData.orders || []);
      setStock(stockData.products || []);
      setStats(statsData);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoading(false);
  }, [filterStatut, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function updateOrderStatus(orderId, statut) {
    try {
      const res = await fetch(`${BACKEND}/api/boutique/admin/commandes/${orderId}/statut`, {
        method: 'PUT', headers,
        body: JSON.stringify({ statut }),
      });
      if (!res.ok) throw new Error();
      toast.success('Statut mis à jour');
      fetchData();
    } catch { toast.error('Erreur mise à jour'); }
  }

  async function saveStock(productId, newStock) {
    try {
      const res = await fetch(`${BACKEND}/api/boutique/admin/stock/${productId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ stock: parseInt(newStock) }),
      });
      if (!res.ok) throw new Error();
      toast.success('Stock mis à jour');
      setEditingStock(prev => { const next = { ...prev }; delete next[productId]; return next; });
      fetchData();
    } catch { toast.error('Erreur mise à jour stock'); }
  }

  const TABS = [
    { id: 'commandes', label: 'Commandes', icon: <ShoppingBag size={16} /> },
    { id: 'stock', label: 'Gestion stock', icon: <Package size={16} /> },
    { id: 'stats', label: 'Statistiques', icon: <TrendingUp size={16} /> },
  ];

  return (
    <div data-testid="admin-boutique">
      {/* Header cockpit */}
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Commandes</p>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, marginTop:4 }}>
          <div>
            <h2 style={{ fontFamily:'var(--admin-font-display)', fontSize:26, fontWeight:700, letterSpacing:'-.6px', color:'var(--admin-text)' }}>
              Boutique & Commandes
            </h2>
            <p style={{ color:'var(--admin-muted)', fontSize:13.5, marginTop:3 }}>
              Commandes en ligne · Gestion des stocks
            </p>
          </div>
          <button onClick={fetchData}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 13px', borderRadius:10,
              border:'1px solid var(--admin-border)', background:'var(--admin-surface)',
              color:'var(--admin-muted)', fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
            <RefreshCw size={13}/> Actualiser
          </button>
        </div>
      </div>

      {/* KPI rapides */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8, marginBottom:16 }}>
          {[
            { label:'CA Boutique',        value:`${stats.ca_boutique?.toFixed(0)||0}€`, color:'var(--admin-gold)' },
            { label:'Commandes payées',   value: stats.commandes_payees||0,             color:'var(--admin-green)' },
            { label:'En attente',         value: stats.commandes_en_attente||0,         color:'var(--admin-amber)' },
            { label:'Expédiées',          value: stats.commandes_expediees||0,          color:'var(--admin-teal)' },
          ].map((s,i) => (
            <div key={i} className="adm-card" style={{ padding:'12px 14px' }}>
              <p style={{ fontSize:11.5, color:'var(--admin-faint)', marginBottom:4 }}>{s.label}</p>
              <p style={{ fontFamily:'var(--admin-font-display)', fontSize:22, fontWeight:700, color:s.color }}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Onglets */}
      <div style={{ display:'flex', gap:4, padding:'4px', borderRadius:12,
        background:'var(--admin-surface2)', marginBottom:16, width:'fit-content' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:9,
              fontSize:13, fontWeight:600, cursor:'pointer', border:'none', transition:'background .15s',
              background: activeTab===tab.id ? 'var(--admin-teal)' : 'transparent',
              color: activeTab===tab.id ? '#052b25' : 'var(--admin-muted)' }}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {/* ── Commandes ── */}
      {activeTab === 'commandes' && (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {/* Filtre statut */}
          <select value={filterStatut} onChange={e=>setFilterStatut(e.target.value)}
            data-testid="filter-statut"
            style={{ padding:'9px 12px', borderRadius:10, background:'var(--admin-surface)',
              border:'1px solid var(--admin-border-soft)', color:'var(--admin-text)', fontSize:13,
              outline:'none', width:'fit-content' }}>
            <option value="">Tous les statuts</option>
            {Object.entries(STATUT_CONFIG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>

          {loading ? (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {[1,2,3].map(i => <div key={i} className="adm-card" style={{ height:72, background:'var(--admin-surface2)' }}/>)}
            </div>
          ) : orders.length === 0 ? (
            <div style={{ textAlign:'center', padding:'50px 20px', color:'var(--admin-faint)' }}>
              <ShoppingBag size={36} style={{ margin:'0 auto 12px', opacity:.2 }}/>
              <p style={{ fontSize:14 }}>Aucune commande trouvée</p>
            </div>
          ) : (
            orders.map(order => {
              const cfg = STATUT_CONFIG[order.statut] || STATUT_CONFIG.en_attente_paiement;
              const isExpanded = expandedOrder === order.id;
              return (
                <div key={order.id} className="adm-card" style={{ overflow:'hidden' }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'13px 15px' }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap', marginBottom:3 }}>
                        <span style={{ fontSize:11, fontWeight:700, color:'var(--admin-gold)',
                          fontFamily:'var(--admin-font-display)', letterSpacing:'.04em' }}>{order.numero}</span>
                        <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:7,
                          background:cfg.bg, color:cfg.color }}>{cfg.label}</span>
                      </div>
                      <p style={{ fontSize:13.5, fontWeight:600, color:'var(--admin-text)', marginBottom:2 }}>
                        {order.client_nom}
                      </p>
                      <p style={{ fontSize:12, color:'var(--admin-muted)' }}>
                        {order.client_email} · {formatDateTime(order.created_at)}
                      </p>
                    </div>
                    {/* Total + actions */}
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <p style={{ fontFamily:'var(--admin-font-display)', fontSize:15, fontWeight:700,
                        color:'var(--admin-gold)', marginBottom:8 }}>
                        {order.total?.toFixed(2)}€
                      </p>
                      <div style={{ display:'flex', gap:5, justifyContent:'flex-end' }}>
                        {/* Changer statut */}
                        <select value={order.statut} onChange={e=>updateOrderStatus(order.id, e.target.value)}
                          style={{ fontSize:11, padding:'4px 8px', borderRadius:8,
                            background:'var(--admin-surface2)', border:'1px solid var(--admin-border)',
                            color:'var(--admin-text)', outline:'none' }}>
                          {Object.entries(STATUT_CONFIG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <button onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                          style={{ width:28, height:28, borderRadius:8, border:'1px solid var(--admin-border)',
                            background:'var(--admin-surface2)', color:'var(--admin-muted)', cursor:'pointer',
                            display:'flex', alignItems:'center', justifyContent:'center' }}>
                          <ChevronDown size={13} style={{ transform:isExpanded?'rotate(180deg)':'none', transition:'transform .2s' }}/>
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Détail articles */}
                  {isExpanded && (
                    <div style={{ padding:'0 15px 13px', borderTop:'1px solid var(--admin-border-soft)' }}>
                      <p style={{ fontSize:11, fontWeight:700, color:'var(--admin-muted)', margin:'10px 0 8px',
                        textTransform:'uppercase', letterSpacing:'.08em' }}>Détail commande</p>
                      {(order.items||[]).map((item,i) => (
                        <div key={i} style={{ display:'flex', justifyContent:'space-between',
                          padding:'5px 0', fontSize:13, borderBottom:'1px solid var(--admin-border-soft)' }}>
                          <span style={{ color:'var(--admin-text)' }}>{item.nom} × {item.quantite}</span>
                          <span style={{ fontWeight:700, color:'var(--admin-gold)' }}>{item.sous_total?.toFixed(2)}€</span>
                        </div>
                      ))}
                      {order.client_adresse && (
                        <p style={{ fontSize:12, color:'var(--admin-muted)', marginTop:8 }}>
                          📍 {order.client_adresse}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── Stock ── */}
      {activeTab === 'stock' && (
        <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
          {loading ? (
            <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
              {[1,2,3,4].map(i => <div key={i} className="adm-card" style={{ height:64, background:'var(--admin-surface2)' }}/>)}
            </div>
          ) : stock.length === 0 ? (
            <div style={{ textAlign:'center', padding:'50px 20px', color:'var(--admin-faint)' }}>
              <Package size={36} style={{ margin:'0 auto 12px', opacity:.2 }}/>
              <p style={{ fontSize:14 }}>Aucun produit de vente</p>
            </div>
          ) : stock.map(p => {
            const isEditing = editingStock[p.id] !== undefined;
            const dispo = p.stock_disponible;
            const hasAlert = p.alerte;
            const alertColor = hasAlert==='rupture' ? 'var(--admin-red)' : hasAlert==='faible' ? 'var(--admin-amber)' : 'var(--admin-green)';
            return (
              <div key={p.id} className="adm-card"
                style={{ padding:'12px 14px', display:'flex', alignItems:'center', gap:12,
                  borderColor: hasAlert==='rupture' ? 'rgba(235,92,92,.3)'
                    : hasAlert==='faible' ? 'rgba(227,165,58,.3)'
                    : 'var(--admin-border-soft)' }}>
                {p.images?.[0] && (
                  <img src={p.images[0]} alt={p.nom} loading="lazy"
                    style={{ width:44, height:44, borderRadius:10, objectFit:'cover', flexShrink:0 }}/>
                )}
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontSize:14, fontWeight:600, color:'var(--admin-text)', marginBottom:3 }}>{p.nom}</p>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    {hasAlert === 'rupture' && (
                      <span style={{ fontSize:11.5, fontWeight:700, color:'var(--admin-red)' }}>
                        <AlertCircle size={11} style={{ display:'inline', marginRight:3 }}/>Rupture
                      </span>
                    )}
                    {hasAlert === 'faible' && (
                      <span style={{ fontSize:11.5, fontWeight:700, color:'var(--admin-amber)' }}>
                        <AlertCircle size={11} style={{ display:'inline', marginRight:3 }}/>Faible ({dispo})
                      </span>
                    )}
                    {!hasAlert && dispo != null && (
                      <span style={{ fontSize:11.5, color:'var(--admin-green)' }}>{dispo} disponibles</span>
                    )}
                    {dispo == null && (
                      <span style={{ fontSize:11.5, color:'var(--admin-muted)' }}>Stock illimité</span>
                    )}
                  </div>
                </div>
                {/* Édition stock — logique intacte */}
                <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                  {isEditing ? (
                    <>
                      <input type="number" min="0" value={editingStock[p.id]}
                        onChange={e=>setEditingStock(prev=>({...prev,[p.id]:e.target.value}))}
                        style={{ width:70, padding:'6px 10px', borderRadius:8, border:'1px solid var(--admin-border)',
                          background:'var(--admin-surface2)', color:'var(--admin-text)', fontSize:13, outline:'none' }}/>
                      <button onClick={()=>saveStock(p.id, editingStock[p.id])}
                        style={{ width:30, height:30, borderRadius:8, border:'1px solid var(--admin-green)',
                          background:'var(--admin-green-dim)', color:'var(--admin-green)', cursor:'pointer',
                          display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <Save size={13}/>
                      </button>
                      <button onClick={()=>setEditingStock(prev=>{const n={...prev};delete n[p.id];return n;})}
                        style={{ width:30, height:30, borderRadius:8, border:'1px solid var(--admin-border)',
                          background:'var(--admin-surface2)', color:'var(--admin-muted)', cursor:'pointer',
                          display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <X size={13}/>
                      </button>
                    </>
                  ) : (
                    <>
                      <p style={{ fontFamily:'var(--admin-font-display)', fontSize:16, fontWeight:700,
                        color:'var(--admin-text)', minWidth:36, textAlign:'right' }}>
                        {p.stock_max!=null ? p.stock_max : '∞'}
                      </p>
                      <button onClick={()=>setEditingStock(prev=>({...prev,[p.id]:p.stock_max??0}))}
                        style={{ width:30, height:30, borderRadius:8, border:'1px solid var(--admin-border)',
                          background:'var(--admin-surface2)', color:'var(--admin-muted)', cursor:'pointer',
                          display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <Edit2 size={13}/>
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Stats ── */}
      {activeTab === 'stats' && stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:12 }}>
          <div className="adm-card" style={{ padding:'16px' }}>
            <p style={{ fontSize:13, fontWeight:700, color:'var(--admin-gold)', marginBottom:14 }}>
              Répartition des commandes
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {Object.entries(STATUT_CONFIG).map(([k,v]) => {
                const count = k==='paye' ? stats.commandes_payees
                  : k==='en_attente_paiement' ? stats.commandes_en_attente
                  : k==='expedie' ? stats.commandes_expediees : null;
                if (count==null) return null;
                return (
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:13, color:'var(--admin-muted)' }}>{v.label}</span>
                    <span style={{ fontFamily:'var(--admin-font-display)', fontSize:18, fontWeight:700, color:v.color }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="adm-card" style={{ padding:'16px' }}>
            <p style={{ fontSize:13, fontWeight:700, color:'var(--admin-gold)', marginBottom:10 }}>
              CA Boutique
            </p>
            <p style={{ fontFamily:'var(--admin-font-display)', fontSize:36, fontWeight:700,
              color:'var(--admin-gold)', marginBottom:6 }}>
              {stats.ca_boutique?.toFixed(0)||0}€
            </p>
            <p style={{ fontSize:13, color:'var(--admin-muted)' }}>
              Commandes payées · expédiées · livrées
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
